import * as React from 'react';
import * as classNames from 'classnames';
import Captcha from '../../captcha/captcha';
import {GDPR_ENABLED} from '../../../utils/config';
import {GutterSize} from '../../../common/enums';
import {Input} from '../../input/input';
import {Form, IFormComponentProps, IFormField} from '../../form/form';
import {IUserRequest, IUserForm} from '../../../common/types';
import {MarketingConsent} from '../../marketing-consent/marketing-consent';
import {TermsConsent} from '../../terms-consent/terms-consent';
import {AuthDivider as Divider} from '../divider';
import {AuthSocial as Social} from '../social/social';
import {AuthButton as Button} from '../button';
import {COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA, KEY_ENTER} from '../service';

import {
    inputGroup as cssInputGroup,
    input      as cssInput,
    consents   as cssConsents,
    marketing  as cssMarketing,
    terms      as cssTerms,
} from './sign-up-form.module.sass';

interface IAuthSignUpFormFields {
  marketingAccepted?: IFormField;
  termsAccepted?: IFormField;
  firstName?: IFormField;
  lastName?: IFormField;
  password?: IFormField;
  email?: IFormField;
}

export const SIGNUP_FORM_DATA: IUserForm = {
  marketingAccepted: false,
  termsAccepted: false,
  firstName: '',
  lastName: '',
  password: '',
  email: '',
};

export interface IAuthSignUpFormButtonProps {
  tooltipDisabled?: boolean;
  tooltipLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  type?: string;
}

export interface IAuthSignUpFormProps extends IFormComponentProps {
  socialAuthEnabled?: boolean;
  firstNameError?: string;
  emailDisabled?: boolean;
  lastNameError?: string;
  passwordError?: string;
  socialError?: string;
  prampError?: string;
  emailError?: string;
  isLoading?: boolean;
  formData: IUserForm;
  renderButton?: (props: IAuthSignUpFormButtonProps) => void;
  onSend: (payload: IUserRequest) => void;
}

interface IAuthSignUpFormState {
  isCaptchaValid?: boolean;
  submitCounter?: number;
}

class SignUpForm extends React.Component<IAuthSignUpFormProps, IAuthSignUpFormState> {
  public state: IAuthSignUpFormState = {
    isCaptchaValid: false,
    submitCounter: 0,
  };

  private elementFirstName: HTMLElement;

  public componentDidMount(): void {
    this.elementFirstName.focus();
  }

  public render(): React.ReactElement<any> {
    const {
      fieldNameWithFirstError,
      socialAuthEnabled,
      firstNameError,
      lastNameError,
      passwordError,
      emailDisabled,
      socialError,
      prampError,
      emailError,
      isLoading,
      renderButton,
      onChangeFormField,
    } = this.props;

    const {
      marketingAccepted,
      termsAccepted,
      firstName,
      lastName,
      password,
      email,
    } = this.formFields();

    const buttonDisabled = GDPR_ENABLED && !termsAccepted.value;
    const buttonTooltipError = buttonDisabled ? 'Please accept our terms and conditions' : null;

    return (
      <form onSubmit={this.handleSubmit}>
        {socialAuthEnabled && (
          <>
            <Social
              tooltipDisabled={!buttonDisabled}
              tooltipLabel={buttonTooltipError}
              externalError={socialError}
              disabled={buttonDisabled}
              onAuth={this.handleSocialAuth}
            />
            <Divider>or</Divider>
          </>
        )}

        <div className={cssInputGroup}>
          <Input
            bottomSpacing={GutterSize.MEDIUM}
            autocomplete={'given-name'}
            className={cssInput}
            placeholder={'First name'}
            reference={node => this.elementFirstName = node}
            value={firstName.value}
            error={firstName.error || firstNameError}
            focused={fieldNameWithFirstError === 'firstName'}
            type={'text'}
            name={'firstName'}
            onKeyDown={this.onKeyDown}
            onChange={value => onChangeFormField('firstName', value)}
          />

          <Input
            bottomSpacing={GutterSize.MEDIUM}
            autocomplete={'family-name'}
            className={cssInput}
            placeholder={'Last name'}
            focused={fieldNameWithFirstError === 'lastName'}
            value={lastName.value}
            error={lastName.error || lastNameError}
            type={'text'}
            name={'lastName'}
            onKeyDown={this.onKeyDown}
            onChange={value => onChangeFormField('lastName', value)}
          />
        </div>

        {emailDisabled ? (
          <Input
            bottomSpacing={GutterSize.MEDIUM}
            placeholder={'Email'}
            disabled
            value={email.value}
            type={'text'}
            name={'email'}
          />
        ) : (
          <Input
            bottomSpacing={GutterSize.MEDIUM}
            autocomplete={'email'}
            placeholder={'Email'}
            focused={fieldNameWithFirstError === 'email'}
            value={email.value}
            error={email.error || emailError}
            type={'text'}
            name={'email'}
            onKeyDown={this.onKeyDown}
            onChange={value => onChangeFormField('email', value)}
          />
        )}

        <Input
          bottomSpacing={GutterSize.MEDIUM}
          autocomplete={'new-password'}
          placeholder={'Password'}
          focused={fieldNameWithFirstError === 'password'}
          value={password.value}
          error={password.error || passwordError}
          type={'password'}
          name={'password'}
          onKeyDown={this.onKeyDown}
          onChange={value => onChangeFormField('password', value)}
        />

        <div className={cssConsents}>
          {GDPR_ENABLED && (
            <MarketingConsent
              className={cssMarketing}
              name='marketing'
              checked={marketingAccepted.value}
              onChange={event => onChangeFormField('marketingAccepted', event.target.checked)}
            />
          )}

          <TermsConsent
            className={cssTerms}
            name='terms'
            checked={termsAccepted.value}
            gdpr={GDPR_ENABLED}
            onChange={event => onChangeFormField('termsAccepted', event.target.checked)}
          />
        </div>

        <Captcha
          className={classNames({'has-error': this.isInvalidCaptcha()})}
          isVisible={this.isVisibleCaptcha()}
          verifyCallback={this.handleVerify}
        />

        {typeof renderButton === 'function' ? (
          renderButton({
            tooltipLabel: buttonTooltipError,
            tooltipDisabled: !buttonDisabled,
            isLoading,
            disabled: buttonDisabled,
            error: prampError,
            type: 'submit',
          })
        ) : (
          <Button
            tooltipDisabled={!buttonDisabled}
            tooltipLabel={buttonTooltipError}
            isLoading={isLoading}
            disabled={buttonDisabled}
            error={prampError}
            type={'submit'}
          />
        )}
      </form>
    );
  }

  private formFields = (): IAuthSignUpFormFields => (
    this.props.fields.reduce((res, next) => ({...res, [next.fieldName]: next}), {})
  )

  private onKeyDown = (event: React.KeyboardEvent<any>): void => {
    if (event.keyCode === KEY_ENTER) {
      event.preventDefault();
      this.register();
    }
  };

  private handleSocialAuth = (payload: IUserRequest) => {
    const {marketingAccepted, termsAccepted} = this.formFields();

    this.props.onSend({
      ...payload,
      marketingAccepted: marketingAccepted.value,
      termsAccepted: termsAccepted.value,
    });
  }

  private handleSubmit = (event: React.MouseEvent<HTMLFormElement>) => {
    event.preventDefault();
    this.register();
  }

  private register = (): void => {
    const isValid: boolean = this.props.isFormValid();
    const {submitCounter, isCaptchaValid} = this.state;

    if (!isValid) {
      return;
    }

    if (isCaptchaValid) {
      this.send();
    } else {
      submitCounter < COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA
        && this.setState({submitCounter: submitCounter + 1}, () => this.send());
    }
  };

  private send = () => {
    // always send marketingAccepted for EU users
    // to make sure that consent is always stored
    const marketingAccepted = GDPR_ENABLED ? this.props.getFieldValue('marketingAccepted') : undefined;
    const fields = this.props.getDirtyFields();
    const email = typeof fields.email === 'string' ? {email: fields.email.trim()} : {};

    this.props.onSend({...fields, ...email, marketingAccepted} as IUserRequest);
  }

  private handleVerify = (): void => (
    this.setState({isCaptchaValid: true})
  )

  private isVisibleCaptcha = (): boolean => (
    !this.props.prampError
      && this.state.submitCounter >= COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA
  )

  private isInvalidCaptcha = (): boolean => (
    !this.state.isCaptchaValid
      && this.state.submitCounter > COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA
  )
}

/**
 * @TODO Better to use Formik instead of custom solution
 */
export const AuthSignUpForm = Form(SignUpForm);
